import type { RouterInputs, RouterOutputs } from '@klear/ui/lib/trpc'
import type { CurrencyCode } from '@klear/constants/currency'
import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast, createToastConfig } from '@klear/ui/hooks/use-mutations-with-toast'
import { useMemo, useState } from 'react'
import { Money } from '@klear/money'
import isArray from 'lodash/isArray'
import toNumber from 'lodash/toNumber'

type CustomerList = RouterOutputs['customer']['list']
type CustomerMutationResult = RouterOutputs['customer']['create']
type CustomerCreateInput = RouterInputs['customer']['create']
type CustomerUpdateInput = RouterInputs['customer']['update']
type CustomerPreviewInput = RouterInputs['customer']['preview']
type CustomerPreviewResult = RouterOutputs['customer']['preview']

export function savedCustomerOptions(customers: CustomerList | undefined): { value: number; label: string }[] {
  return (customers ?? []).flatMap((party) =>
    party.identity.kind === 'saved' ? [{ value: party.identity.id, label: party.name }] : []
  )
}

export function useCustomers(traderId?: number): {
  customers: CustomerList
  error: ReturnType<typeof trpc.customer.list.useQuery>['error']
  isLoading: boolean
} {
  const { data, error, isLoading } = trpc.customer.list.useQuery({
    traderId
  })

  return { customers: data ?? [], error, isLoading }
}

function purchaseOrderVolumesByCustomerId(
  input:
    | RouterOutputs['customer']['purchaseOrderVolumes']
    | RouterOutputs['customer']['purchaseOrderVolumesListAll']
    | null
    | undefined
): Map<number, Money> {
  const rows = isArray(input) ? input : (input?.volumes ?? [])
  const map = new Map<number, Money>()
  for (const row of rows) {
    map.set(toNumber(row.customerId), new Money(row.volume))
  }
  return map
}

export function useCustomerPurchaseOrderVolumes(
  customerIds: readonly number[],
  options?: { traderId?: number; currency?: CurrencyCode; enabled?: boolean }
): {
  volumesByCustomerId: Map<number, Money>
  error: ReturnType<typeof trpc.customer.purchaseOrderVolumes.useQuery>['error']
  isLoading: boolean
} {
  const ids = useMemo(() => [...new Set(customerIds.filter((id) => id > 0))], [customerIds])
  const { data, error, isLoading } = trpc.customer.purchaseOrderVolumes.useQuery(
    {
      customerIds: ids,
      traderId: options?.traderId,
      currency: options?.currency
    },
    {
      enabled: (options?.enabled ?? true) && ids.length > 0,
      select: purchaseOrderVolumesByCustomerId
    }
  )

  return { volumesByCustomerId: data ?? new Map(), error, isLoading }
}

export function useCustomerPurchaseOrderVolumesListAll(options?: {
  traderId?: number
  currency?: CurrencyCode
  enabled?: boolean
}): {
  volumesByCustomerId: Map<number, Money>
  availableCurrencies: CurrencyCode[]
  error: ReturnType<typeof trpc.customer.purchaseOrderVolumesListAll.useQuery>['error']
  isLoading: boolean
} {
  const input = useMemo(() => {
    const value: { traderId?: number; currency?: CurrencyCode } = {}
    if (options?.traderId != null) value.traderId = options.traderId
    if (options?.currency != null) value.currency = options.currency
    return value
  }, [options?.traderId, options?.currency])

  const { data, error, isLoading } = trpc.customer.purchaseOrderVolumesListAll.useQuery(input, {
    enabled: options?.enabled ?? true,
    select: (payload) => {
      const envelope = isArray(payload) ? { volumes: payload, currencies: [] as CurrencyCode[] } : payload
      return {
        volumesByCustomerId: purchaseOrderVolumesByCustomerId(envelope),
        availableCurrencies: (envelope.currencies ?? []) as CurrencyCode[]
      }
    }
  })

  return {
    volumesByCustomerId: data?.volumesByCustomerId ?? new Map(),
    availableCurrencies: data?.availableCurrencies ?? [],
    error,
    isLoading
  }
}

type CustomerOfferForecastRow = RouterOutputs['customer']['offerForecasts'][number]
type CustomerOfferForecast = Omit<CustomerOfferForecastRow, 'openExposure'> & { openExposure: Money }

function offerForecastsByCustomerId(rows: CustomerOfferForecastRow[]): Map<number, CustomerOfferForecast> {
  const map = new Map<number, CustomerOfferForecast>()
  for (const row of rows) {
    map.set(row.customerId, {
      ...row,
      openExposure: new Money(row.openExposure)
    })
  }
  return map
}

export function useCustomerOfferForecasts(
  customerIds: readonly number[],
  options?: { traderId?: number; enabled?: boolean }
): {
  forecastsByCustomerId: Map<number, CustomerOfferForecast>
  error: ReturnType<typeof trpc.customer.offerForecasts.useQuery>['error']
  isLoading: boolean
} {
  const ids = useMemo(() => [...new Set(customerIds.filter((id) => id > 0))], [customerIds])
  const { data, error, isLoading } = trpc.customer.offerForecasts.useQuery(
    {
      customerIds: ids,
      traderId: options?.traderId
    },
    {
      enabled: (options?.enabled ?? true) && ids.length > 0,
      select: offerForecastsByCustomerId
    }
  )

  return { forecastsByCustomerId: data ?? new Map(), error, isLoading }
}

export function useCustomerOfferForecastsListAll(options?: { traderId?: number; enabled?: boolean }): {
  forecastsByCustomerId: Map<number, CustomerOfferForecast>
  error: ReturnType<typeof trpc.customer.offerForecastsListAll.useQuery>['error']
  isLoading: boolean
} {
  const { data, error, isLoading } = trpc.customer.offerForecastsListAll.useQuery(
    {
      traderId: options?.traderId
    },
    {
      enabled: options?.enabled ?? true,
      select: offerForecastsByCustomerId
    }
  )

  return { forecastsByCustomerId: data ?? new Map(), error, isLoading }
}

type CustomerOfferHistoryRow = {
  customerId: number
  offerCount: number
  invoiceValue: Money
  fundedValue: Money
}

export function useCustomerOfferHistoryListAll(options?: {
  traderId?: number
  currency?: CurrencyCode
  enabled?: boolean
}): {
  historyByCustomerId: Map<number, CustomerOfferHistoryRow>
  availableCurrencies: CurrencyCode[]
  error: ReturnType<typeof trpc.customer.offerHistoryListAll.useQuery>['error']
  isLoading: boolean
} {
  const input = useMemo(() => {
    const value: { traderId?: number; currency?: CurrencyCode } = {}
    if (options?.traderId != null) value.traderId = options.traderId
    if (options?.currency != null) value.currency = options.currency
    return value
  }, [options?.traderId, options?.currency])

  const { data, error, isLoading } = trpc.customer.offerHistoryListAll.useQuery(input, {
    enabled: options?.enabled ?? true,
    select: (payload) => {
      const map = new Map<number, CustomerOfferHistoryRow>()
      for (const row of payload.history) {
        map.set(row.customerId, {
          customerId: row.customerId,
          offerCount: row.offerCount,
          invoiceValue: new Money(row.invoiceValue),
          fundedValue: new Money(row.fundedValue)
        })
      }
      return {
        historyByCustomerId: map,
        availableCurrencies: payload.currencies as CurrencyCode[]
      }
    }
  })

  return {
    historyByCustomerId: data?.historyByCustomerId ?? new Map(),
    availableCurrencies: data?.availableCurrencies ?? [],
    error,
    isLoading
  }
}

export function useCustomer(id: number, options?: { enabled?: boolean; traderId?: number }) {
  const {
    data: customer,
    error,
    isLoading
  } = trpc.customer.get.useQuery(
    { id, traderId: options?.traderId },
    {
      enabled: !!id && options?.enabled !== false
    }
  )

  return { customer, error, isLoading }
}

const invalidateQueries = [
  'customer.list',
  'customer.get',
  'customer.purchaseOrderVolumes',
  'customer.purchaseOrderVolumesListAll',
  'customer.offerForecasts',
  'customer.offerForecastsListAll',
  'customer.offerHistory',
  'customer.offerHistoryListAll'
]

export function useCustomerMutations() {
  const customerName = (data: CustomerMutationResult) => data.name ?? ''
  const utils = trpc.useUtils()
  const [isPreviewing, setIsPreviewing] = useState(false)

  const createMutation = useMutationWithToast(
    trpc.customer.create.useMutation,
    createToastConfig<CustomerMutationResult>('Customer', 'create', customerName),
    { invalidateQueries }
  )
  const updateMutation = useMutationWithToast(
    trpc.customer.update.useMutation,
    createToastConfig<CustomerMutationResult>('Customer', 'update', customerName),
    { invalidateQueries }
  )

  const deleteCustomerMutation = useMutationWithToast(
    trpc.customer.delete.useMutation,
    createToastConfig('Customer', 'delete'),
    {
      invalidateQueries
    }
  )

  const previewCustomerCall = async (input: CustomerPreviewInput): Promise<CustomerPreviewResult> => {
    setIsPreviewing(true)
    try {
      return await utils.customer.preview.fetch(input)
    } finally {
      setIsPreviewing(false)
    }
  }

  const previewNewCustomer = (
    input: Extract<CustomerPreviewInput, { id?: undefined }>
  ): Promise<CustomerPreviewResult> => previewCustomerCall(input)

  const previewCustomer = (input: Extract<CustomerPreviewInput, { id: number }>): Promise<CustomerPreviewResult> =>
    previewCustomerCall(input)

  const commitNewCustomer = (input: CustomerCreateInput): Promise<CustomerMutationResult> =>
    createMutation.mutateAsync(input)

  const commitCustomer = (input: CustomerUpdateInput): Promise<CustomerMutationResult> =>
    updateMutation.mutateAsync(input)

  return {
    previewNewCustomer,
    previewCustomer,
    commitNewCustomer,
    commitCustomer,
    deleteCustomer: deleteCustomerMutation.mutateAsync,
    isPreviewing,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteCustomerMutation.isPending
  }
}
